/**
 * Öffentliche Tracking-Seite: /t/<slug> (Suche), /t/<slug>/<token> (persönlicher Link) bzw. auf der eigenen Domain
 * des Kunden / und /<token>. Sprache nach Lieferland, umschaltbar; nichts aus dem Portal (kein Login, keine Navigation).
 */
import type { TrackingOrderView, TrackingPageLang, TrackingSite } from "@logiful/domain";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { api, ApiError } from "@/lib/api";
import { TrackingShell, TrackingView } from "./TrackingView";

/** Auf eigener Domain liefert der Server index.html mit diesem Kennzeichen aus */
export const trackingHostSlug = (): string | null => document.querySelector<HTMLMetaElement>('meta[name="lc-tracking-site"]')?.content || null;

const browserLang = (): TrackingPageLang => (navigator.languages ?? [navigator.language]).some((l) => l?.toLowerCase().startsWith("de")) ? "de" : "en";

export function PublicTrackingPage({ hostSlug }: { hostSlug?: string | null }) {
  const params = useParams();
  const slug = hostSlug ?? params.slug ?? "";
  const token = params.token ?? null;
  const navigate = useNavigate();
  const base = hostSlug ? "" : `/t/${slug}`;
  const preselect = new URLSearchParams(location.search).get("p");

  const site = useQuery({ queryKey: ["public-tracking", slug], queryFn: () => api.publicTracking.site(hostSlug ? null : slug), retry: false, enabled: !token });
  const order = useQuery({ queryKey: ["public-tracking", slug, token], queryFn: () => api.publicTracking.order(slug, token!), retry: false, enabled: !!token, refetchInterval: 5 * 60_000 });

  const s: TrackingSite | undefined = token ? order.data?.site : site.data;
  const o: TrackingOrderView | null = token ? (order.data?.order ?? null) : null;
  const [lang, setLang] = useState<TrackingPageLang>(browserLang());
  useEffect(() => {
    if (o) setLang(o.lang);
  }, [o?.lang]);

  useEffect(() => {
    if (!s) return;
    const title = s.config.title[lang] || s.config.title.de || s.shop;
    document.title = o ? `${o.orderNumber} · ${title}` : title;
    document.documentElement.lang = lang;
    let meta = document.querySelector<HTMLMetaElement>('meta[name="robots"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "robots";
      document.head.appendChild(meta);
    }
    meta.content = "noindex";
  }, [s, o, lang]);

  const failed = token ? order.isError : site.isError;
  if (failed) {
    const notFound = (token ? order.error : site.error) instanceof ApiError;
    return <TrackingShell>{notFound ? (lang === "de" ? "Diese Seite gibt es nicht oder der Link ist abgelaufen." : "This page doesn't exist or the link has expired.") : lang === "de" ? "Die Seite ist gerade nicht erreichbar. Bitte versuche es gleich noch einmal." : "The page is temporarily unavailable. Please try again shortly."}</TrackingShell>;
  }
  if (!s) return <TrackingShell><span className="inline-block size-6 animate-spin rounded-full border-2 border-current border-t-transparent opacity-40" /></TrackingShell>;

  return (
    <TrackingView
      config={s.config}
      shop={s.shop}
      lang={lang}
      onLang={setLang}
      asset={(id) => (s.assetBase ? `${s.assetBase}/${id}` : api.trackingPage.assetUrl(id))}
      order={o}
      preselect={preselect}
      search={{
        enabled: s.search,
        onSubmit: async (n, c) => {
          try {
            const r = await api.publicTracking.lookup(slug, n, c);
            navigate(`${base}/${r.token}`);
            return "ok";
          } catch (e) {
            return e instanceof ApiError && e.status === 429 ? "rate_limited" : "not_found";
          }
        },
      }}
    />
  );
}
